import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, DollarSign, Send, ChevronDown, ChevronUp, CreditCard, FileText } from "lucide-react";
import PaymentSelector from "@/components/PaymentSelector";
import CombineInvoiceModal from "@/components/CombineInvoiceModal";

/**
 * Compact KPI tile shown on the dispatcher board. Surfaces the still-owed
 * receivables at a glance and lets the dispatcher fire a one-tap payment
 * blast to every reachable unpaid customer.
 */
export default function UnpaidAgingTile() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [payTrip, setPayTrip] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [combining, setCombining] = useState(false);
  const togglePick = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const reload = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/unpaid-aging");
      setData(r.data);
    } catch (e) {
      // Silently fail — tile just shows nothing rather than crashing the board
    } finally { setBusy(false); }
  };

  useEffect(() => { reload(); }, []);

  if (!data) return null;
  if (data.trip_count === 0) {
    return (
      <div data-testid="unpaid-aging-tile" className="mb-6 border border-emerald-500/20 bg-emerald-500/5 px-5 py-3 flex items-center gap-3 text-[12px]">
        <DollarSign size={14} className="text-emerald-300"/>
        <span className="text-emerald-200/80 uppercase tracking-[0.18em] text-[10px]">
          All clear · no unpaid trips
        </span>
      </div>
    );
  }

  const blast = async () => {
    if (!window.confirm(
      `Send a payment reminder (SMS + email) to all ${data.trip_count} unpaid customer(s)?`
    )) return;
    setBlasting(true);
    try {
      const r = await api.post("/admin/unpaid-aging/blast");
      toast.success(`Reminders sent · ${r.data.sent} reached · ${r.data.skipped} unreachable · ${r.data.failed} failed`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Blast failed");
    } finally { setBlasting(false); }
  };

  return (
    <div data-testid="unpaid-aging-tile" className="mb-6 border border-[#D4AF37]/30 bg-gradient-to-r from-[#D4AF37]/10 via-[#D4AF37]/5 to-transparent">
      <div className="px-5 py-4 flex items-center gap-5 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <DollarSign size={16} className="text-[#D4AF37]"/>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">Unpaid aging</span>
        </div>

        <div className="flex items-baseline gap-2">
          <span className="font-serif text-3xl text-[#D4AF37]" data-testid="unpaid-total">
            ${data.total_owed.toFixed(2)}
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">owed</span>
        </div>

        <div className="h-8 w-px bg-white/10 hidden sm:block"/>

        <div className="text-[11px] text-[#A1A1AA]">
          <span className="text-[#F5F2EB] font-semibold" data-testid="unpaid-count">{data.trip_count}</span> trip{data.trip_count === 1 ? "" : "s"}
          <span className="mx-2 text-[#71717A]">·</span>
          <Clock size={11} className="inline mr-1 text-[#D4AF37]/70"/>
          oldest <span className="text-[#F5F2EB] font-semibold" data-testid="unpaid-oldest">{data.oldest_days}</span> day{data.oldest_days === 1 ? "" : "s"}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="unpaid-toggle"
            onClick={() => setExpanded(v => !v)}
            className="btn-ghost !py-1.5 !px-3 text-[10px]"
          >
            {expanded ? <><ChevronUp size={12}/>Hide</> : <><ChevronDown size={12}/>View trips</>}
          </button>
          <button
            data-testid="unpaid-blast"
            disabled={blasting || busy}
            onClick={blast}
            className="btn-gold !py-1.5 !px-3 text-[10px] disabled:opacity-50"
          ><Send size={12}/>{blasting ? "Sending…" : "Send all reminders now"}</button>
        </div>
      </div>

      {expanded && (
        <div data-testid="unpaid-list" className="border-t border-white/10">
          <div className="px-4 py-2 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[#71717A] border-b border-white/5">
            <span>Tick trips for one client, then combine them into a single invoice</span>
            {picked.size > 0 && (
              <>
                <span className="text-[#F5F2EB]" data-testid="unpaid-picked-count">{picked.size} selected</span>
                <button data-testid="unpaid-pick-clear" onClick={() => setPicked(new Set())} className="hover:text-white">Clear</button>
              </>
            )}
            <button data-testid="unpaid-combine-btn" disabled={picked.size < 2} onClick={() => setCombining(true)}
              className="ml-auto btn-gold !py-1 !px-3 text-[10px] disabled:opacity-30 inline-flex items-center gap-1.5">
              <FileText size={11}/>Generate combined invoice{picked.size > 1 ? ` (${picked.size})` : ""}
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-black/40 text-[9px] uppercase tracking-[0.18em] text-[#71717A] sticky top-0">
              <tr>
                <th className="w-8 px-3"></th>
                <th className="text-left px-2 py-2">Code · Pickup</th>
                <th className="text-left px-2">Customer</th>
                <th className="text-right px-2">Fare</th>
                <th className="text-right px-2">Age</th>
                <th className="text-left px-2">Reach</th>
                <th className="text-right px-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.trips.map(t => (
                <tr key={t.booking_id} className={`border-t border-white/5 hover:bg-white/[0.02] ${picked.has(t.booking_id) ? "bg-[#D4AF37]/5" : ""}`} data-testid={`unpaid-row-${t.booking_id}`}>
                  <td className="px-3">
                    <input type="checkbox" data-testid={`unpaid-check-${t.booking_id}`} checked={picked.has(t.booking_id)} onChange={() => togglePick(t.booking_id)}
                      className="accent-[#D4AF37] w-3.5 h-3.5 cursor-pointer"/>
                  </td>
                  <td className="px-2 py-2">
                    <div className="font-mono text-[#D4AF37]">{t.confirmation_code}</div>
                    <div className="text-[10px] text-[#71717A]">{new Date(t.pickup_datetime).toLocaleDateString()} · {t.vehicle_name}{t.consolidated_invoice_number ? ` · ${t.consolidated_invoice_number}` : ""}</div>
                  </td>
                  <td className="px-2 text-[#F5F2EB] truncate max-w-[180px]">{t.customer_name}</td>
                  <td className="px-2 text-right font-mono">${t.fare.toFixed(2)}</td>
                  <td className="px-2 text-right">
                    <span className={`text-[10px] ${t.days_overdue >= 7 ? "text-red-300" : t.days_overdue >= 2 ? "text-amber-300" : "text-[#A1A1AA]"}`}>
                      {t.days_overdue}d
                    </span>
                  </td>
                  <td className="px-2 text-[10px] text-[#A1A1AA] uppercase tracking-[0.1em]">
                    {t.customer_email && t.customer_phone ? "sms · email"
                      : t.customer_phone ? "sms"
                      : t.customer_email ? "email"
                      : <span className="text-red-400/70">none</span>}
                  </td>
                  <td className="px-4 text-right">
                    <button
                      data-testid={`unpaid-pay-${t.booking_id}`}
                      onClick={() => setPayTrip(t)}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline inline-flex items-center gap-1"
                    ><CreditCard size={11}/>Take payment</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {combining && (
        <CombineInvoiceModal
          trips={data.trips.filter(t => picked.has(t.booking_id))}
          onClose={() => setCombining(false)}
          onCreated={() => { setPicked(new Set()); reload(); }}
        />
      )}

      {payTrip && (
        <div
          data-testid="unpaid-pay-modal"
          className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPayTrip(null)}
        >
          <div
            className="bg-[#050505] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="unpaid-pay-modal-close"
              onClick={() => setPayTrip(null)}
              className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white"
              aria-label="Close"
            >✕</button>
            <div className="overline mb-1">Take payment</div>
            <div className="font-serif text-2xl text-[#D4AF37] mb-1">{payTrip.confirmation_code}</div>
            <div className="text-xs text-[#A1A1AA] mb-4">{payTrip.customer_name} · ${payTrip.fare.toFixed(2)}</div>
            <PaymentSelector
              booking={payTrip}
              dispatcherMode
              onPaid={() => { setPayTrip(null); reload(); toast.success("Payment recorded"); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
